import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ShoppingCart, Star, Gift, ArrowRight,
  ChevronLeft, ChevronRight,
  Tent, User, Clock,
} from 'lucide-react';

import { toggleResetPasswordModal } from '../../store/ui';
import { getAllProgramListAction } from '../../store/home';
import { fFilterTypeAction, fCategoriesAction } from '../../store/pFilter';
import { setSubCatAction } from '../../store/program';

import MainSlider from '../reuse/MainSlider';
import MainFilter from './MainFilterNew';
import { ProgramLoader, LogoWhite } from '../reuse/SVG';
import { getAdvertisementService } from '../../services/otherService';
import NearByCarousel from '../carousels/NearByCarousel';

import { getProgramType, dateFormat, getgendersdisplay, dateFormatfordays, getCardPrice } from '../../helper';

import ChessForSuccessColor from '../../assets/img/district/chess-for-success-color.png';
import AddsIcon from '../../assets/img/adds-icon.png';
import sportInternationalImage from '../../assets/img/sport-international.png';

const BRAND   = '#842014';
const BRAND_D = '#6b1a10';
const ORANGE  = '#f9a720';

/* ─── Helpers ─── */
const getPrice = item => {
  if (item?.isSpecialDeal && item?.specialDealsPrice != null) return `$${item.specialDealsPrice.toFixed(2)}`;
  // Gate on the price rendered — `registrationFee` is the payable figure and is 0 whenever
  // `discountType` is neither 1 nor 2, which blanked the price on a program that has one.
  if (getCardPrice(item)) return `$${getCardPrice(item).toFixed(2)}`;
  return '';
};

/* ─── Program Card ─── */
const ProgramCard = ({ data, compact }) => (
  <Link
    to={`/camps/${data._id}`}
    style={{ textDecoration: 'none', display: 'block', flexShrink: compact ? 0 : undefined, width: compact ? 288 : undefined }}
  >
    <div style={{
      background: '#fff', borderRadius: 12,
      overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
      border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column',
      transition: 'box-shadow 0.2s, transform 0.2s',
    }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 28px rgba(0,0,0,0.14)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'none'; }}
    >
      {/* Image */}
      <div style={{ position: 'relative', height: 192, overflow: 'hidden', background: '#0f172a' }}>
        {data.programImage
          ? <img src={data.programImage} alt={data.programName} style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s' }}
              onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'none'} />
          : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><LogoWhite /></div>
        }
        {/* Badge */}
        <div style={{
          position: 'absolute', top: 12, left: 12,
          background: 'rgba(0,0,0,0.60)', backdropFilter: 'blur(4px)',
          color: '#fff', fontSize: '0.75rem', fontWeight: 700,
          padding: '4px 10px', borderRadius: 6,
        }}>{data.camps}</div>
        {/* Title + Price overlay */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, width: '100%',
          background: 'linear-gradient(to top, rgba(0,0,0,0.82), transparent)',
          padding: '28px 16px 14px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
        }}>
          <h3 style={{ color: '#fff', fontWeight: 700, fontSize: '1rem', margin: 0, lineHeight: 1.3, maxWidth: '70%' }}>
            {data.programName}
          </h3>
          <span style={{ color: '#fff', fontWeight: 900, fontSize: '1rem', flexShrink: 0 }}>
            {getPrice(data)}
          </span>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '16px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <p style={{ color: BRAND, fontWeight: 600, fontSize: '0.875rem', margin: 0 }}>
              {dateFormat(data.startDateFormatted)} – {dateFormat(data.endDateFormatted)}
            </p>
            <p style={{ color: '#94a3b8', fontSize: '0.75rem', margin: '2px 0 0' }}>
              ({dateFormatfordays(data.startDateFormatted, data.endDateFormatted)})
            </p>
          </div>
          <span style={{
            background: BRAND, color: '#fff',
            fontSize: '0.75rem', fontWeight: 700,
            padding: '6px 16px', borderRadius: 6,
            cursor: 'pointer', flexShrink: 0,
          }}>Details</span>
        </div>

        {/* Chips */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 'auto', paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
          <Chip icon={<Tent size={13} style={{ color: '#64748b' }} />} label={getProgramType(data.programType)} />
          <Chip icon={<User size={13} style={{ color: '#64748b' }} />} label={`${getgendersdisplay(data.gender) || '—'}`} />
          <Chip icon={<Clock size={13} style={{ color: '#64748b' }} />} label={`${data.checkInTime} – ${data.checkOutTime}`} />
          <Chip icon={<User size={13} style={{ color: '#64748b' }} />} label={`Age: ${data.ageGroup || '—'}`} />
          <div style={{ marginLeft: 'auto' }}>
            <span style={{
              background: '#fff7ed', color: ORANGE,
              fontSize: '0.7rem', fontWeight: 700,
              padding: '4px 10px', borderRadius: 9999,
              display: 'flex', alignItems: 'center', gap: 4,
            }}>EZPro <Star size={11} style={{ fill: ORANGE, color: ORANGE }} /></span>
          </div>
        </div>
      </div>
    </div>
  </Link>
);

const Chip = ({ icon, label }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 6,
    background: '#f1f5f9', color: '#334155',
    padding: '6px 12px', borderRadius: 9999,
    fontSize: '0.75rem', fontWeight: 500,
  }}>
    {icon}<span>{label}</span>
  </div>
);

/* ─── Browse by Type ─── */
const BrowseByType = ({ category, fCategoriesAction, setSubCatAction }) => {
  const scrollRef = useRef(null);
  const history = useHistory();

  const scroll = dir => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: dir === 'left' ? -300 : 300, behavior: 'smooth' });
  };

  const handleCatPage = cat => {
    const subCatArr = [{ categoryname: cat.name, catlist: cat.subCategory }];
    setSubCatAction(subCatArr);
    fCategoriesAction([cat._id]);
    history.push('/camps');
  };

  return (
    <section style={{ padding: '48px 0' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 32 }}>
          <div>
            <h2 style={{ fontSize: '1.875rem', fontWeight: 700, color: '#111827', margin: '0 0 8px', position: 'relative', display: 'inline-block' }}>
              Browse by type
              <div style={{ position: 'absolute', bottom: -8, left: 0, width: '50%', height: 4, background: BRAND, borderRadius: 9999 }} />
            </h2>
            <p style={{ color: '#6b7280', margin: '16px 0 0', fontSize: '0.9375rem' }}>
              Choose your favorite category and find out what's new!
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {[['left', ChevronLeft], ['right', ChevronRight]].map(([dir, Icon]) => (
              <button key={dir} onClick={() => scroll(dir)} style={{
                width: 40, height: 40, borderRadius: '50%', border: `1px solid ${BRAND}`,
                color: BRAND, background: 'transparent', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background 0.2s, color 0.2s',
              }}
                onMouseEnter={e => { e.currentTarget.style.background = BRAND; e.currentTarget.style.color = '#fff'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = BRAND; }}
              >
                <Icon size={18} />
              </button>
            ))}
          </div>
        </div>

        <div ref={scrollRef} style={{
          display: 'flex', gap: 24, overflowX: 'auto', paddingBottom: 16,
          scrollbarWidth: 'none', msOverflowStyle: 'none',
          scrollSnapType: 'x mandatory',
        }}>
          {category.map((cat, idx) => (
            <motion.div
              key={cat._id}
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              onClick={() => handleCatPage(cat)}
              whileHover="hover"
              style={{
                position: 'relative', flexShrink: 0,
                width: 256, height: 320, borderRadius: 12,
                overflow: 'hidden', cursor: 'pointer',
                scrollSnapAlign: 'start',
              }}
            >
              {cat.bannerImage
                ? <motion.img
                    src={cat.bannerImage}
                    alt={cat.name}
                    variants={{ hover: { scale: 1.1 } }}
                    transition={{ duration: 0.5 }}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                : <div style={{ width: '100%', height: '100%', background: '#e2e8f0' }} />
              }
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.80) 0%, rgba(0,0,0,0.20) 50%, transparent 100%)' }} />
              <motion.div
                variants={{ hover: { y: -8 } }}
                transition={{ duration: 0.3 }}
                style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', padding: 16 }}
              >
                <h3 style={{ color: '#fff', fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>{cat.name}</h3>
                <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: 4, background: BRAND }} />
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Programs Grid (Featured + Popular) ─── */
const ProgramsGrid = ({ list, title, subtitle, isFeatured, onViewAll }) => {
  const items = list?.data || [];

  return (
    <section style={{ padding: '48px 0', background: isFeatured ? '#fff5f4' : '#fff' }}>
      {isFeatured && (
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: 192, background: 'linear-gradient(to bottom, rgba(249,167,32,0.12), transparent)', pointerEvents: 'none' }} />
      )}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px', position: 'relative', zIndex: 1 }}>
        {isFeatured && (
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <motion.h2
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              onClick={onViewAll}
              style={{ fontSize: 'clamp(1.6rem, 3vw, 2.25rem)', fontWeight: 700, color: BRAND, marginBottom: 8, cursor: 'pointer', position: 'relative', display: 'inline-block' }}
            >
              {title}
              <div style={{ position: 'absolute', bottom: -6, left: '25%', width: '50%', height: 4, background: `${BRAND}4d`, borderRadius: 9999 }} />
            </motion.h2>
            {subtitle && (
              <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
                style={{ color: '#6b7280', margin: '16px 0 0', fontSize: '0.9375rem' }}>
                {subtitle}
              </motion.p>
            )}
          </div>
        )}

        {!isFeatured && (
          <div style={{ marginBottom: 32 }}>
            <h2 onClick={onViewAll} style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', cursor: 'pointer', margin: 0 }}>
              {title}
            </h2>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 24 }}>
          {items.map((item, idx) => (
            <motion.div
              key={item._id || idx}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: idx * 0.12 }}
            >
              <ProgramCard data={item} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Starting Soon ─── */
const StartingSoon = ({ list }) => {
  const scrollRef = useRef(null);
  const items = list?.data || [];

  const scroll = dir => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: dir === 'left' ? -320 : 320, behavior: 'smooth' });
  };

  return (
    <section style={{ padding: '48px 0' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 24, paddingBottom: 16,
          borderBottom: '2px solid #f1f5f9', position: 'relative',
        }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', margin: 0, position: 'relative' }}>
            Starting Soon
            <div style={{ position: 'absolute', bottom: -18, left: 0, width: '100%', height: 4, background: BRAND, borderRadius: 9999 }} />
          </h2>
          <div style={{ display: 'flex', gap: 8 }}>
            {[['left', ChevronLeft], ['right', ChevronRight]].map(([dir, Icon]) => (
              <button key={dir} onClick={() => scroll(dir)} style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: '#9ca3af', padding: 4,
                transition: 'color 0.2s',
              }}
                onMouseEnter={e => e.currentTarget.style.color = BRAND}
                onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}
              >
                <Icon size={24} />
              </button>
            ))}
          </div>
        </div>

        <div ref={scrollRef} style={{
          display: 'flex', gap: 24, overflowX: 'auto', paddingBottom: 16,
          scrollbarWidth: 'none', msOverflowStyle: 'none',
        }}>
          {items.map((item, idx) => (
            <motion.div key={item._id || idx} initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.08 }}>
              <ProgramCard data={item} compact />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Home ─── */
const rewardSteps = [
  { Icon: ShoppingCart, label: 'Enjoy Programs' },
  { Icon: Star,         label: 'Get Rewards' },
  { Icon: Gift,         label: 'Redeem' },
];

const Home = props => {
  const history = useHistory();
  const [loader, setLoader]   = useState(true);
  const [advList, setAdvList] = useState([]);

  useEffect(() => {
    const params = `?lat=${props.lat}&long=${props.long}&limit=5`;
    props.getAllProgramListAction(params).then(() => setLoader(false));
  }, [props.lat]);

  useEffect(() => {
    getAdvertisementService('?pageName=home').then(res => {
      if (res?.data?.statusCode) setAdvList(res.data.responseData.result);
    });
    if (props.isResetPassword) props.toggleResetPasswordModal();
  }, []);

  const handleViewAll = text => {
    if (text === 'Featured Programs') props.fFilterTypeAction(3);
    if (text === 'Popular Programs')  props.fFilterTypeAction(0);
    history.push('/camps');
  };

  return (
    <div style={{ overflow: 'visible', position: 'relative', zIndex: 1, paddingBottom: 50 }}>
      {loader ? (
        <div style={{ position: 'fixed', width: '100%', height: '100%', background: '#fff', zIndex: 9999, top: 0, left: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ProgramLoader />
        </div>
      ) : ''}

      {/* ── Hero ── */}
      <style>{`
        @media (max-width: 767px) {
          .main_filter_mobile_offset { padding-right: 5% !important; }
        }
      `}</style>
      <div className="main_banner">
        <MainSlider />
        <div className="main_filter_wrapper">
          <div className="container h-100">
            <div className="row g-0 align-content-center d-flex h-100">
              <div className="col-xl-5 col-md-6 main_filter_mobile_offset">
                <MainFilter isHome={true} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Premier Partners ── */}
      <section style={{ background: '#f8fafc', padding: '28px 0', borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 24 }}
          >
            <h3 style={{ fontSize: '1.125rem', fontWeight: 700, color: '#1e293b', margin: 0, whiteSpace: 'nowrap' }}>
              Premier Partners:
            </h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 32 }}>
              {/* <Link to="/district-camps">
                <img src={ChessForSuccessColor} alt="Chess for Success" style={{ height: 52, objectFit: 'contain', display: 'block' }} />
              </Link> */}
              <Link to="/sports-international-camps">
                <img src={sportInternationalImage} alt="Sports International" style={{ height: 52, objectFit: 'contain', display: 'block' }} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Rewards Banner ── */}
      <section style={{ padding: '40px 16px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
            style={{
              background: BRAND, color: '#fff', borderRadius: 16, overflow: 'hidden',
              display: 'flex', flexWrap: 'wrap', alignItems: 'center',
              boxShadow: '0 8px 32px rgba(132,32,20,0.25)',
            }}
          >
            <div style={{ flex: 1, minWidth: 260, padding: 'clamp(24px, 5vw, 48px)' }}>
              <h2 style={{ color: 'rgba(255,255,255,0.80)', fontSize: 'clamp(1.4rem, 3vw, 2rem)', fontWeight: 700, marginBottom: 10, fontFamily: "Georgia, 'Times New Roman', serif" }}>
                Introducing Rewards Points!
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.80)', fontSize: '0.9375rem', lineHeight: 1.7, margin: 0, maxWidth: 440 }}>
                Collect points with the purchase of any participating program and redeem them on future purchases.
              </p>
            </div>
            <div style={{ padding: 'clamp(20px, 4vw, 48px)', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
              {rewardSteps.map(({ Icon, label }, i) => (
                <React.Fragment key={label}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(255,255,255,0.20)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
                      <Icon size={24} style={{ color: '#fff' }} />
                    </div>
                    <span style={{ fontWeight: 600, fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>{label}</span>
                  </div>
                  {i < rewardSteps.length - 1 && <ArrowRight size={18} style={{ color: 'rgba(255,255,255,0.40)', flexShrink: 0 }} />}
                </React.Fragment>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Browse by Type ── */}
      <BrowseByType
        category={props.category}
        fCategoriesAction={props.fCategoriesAction}
        setSubCatAction={props.setSubCatAction}
      />

      {/* ── Featured Programs ── */}
      {props.featured?.data?.length > 0 && (
        <ProgramsGrid
          list={props.featured}
          title="Featured Programs"
          subtitle="Get inspiration for your next activity"
          isFeatured
          onViewAll={() => handleViewAll('Featured Programs')}
        />
      )}

      {/* ── Popular Programs ── */}
      {props.popularCamps?.data?.length > 0 && (
        <ProgramsGrid
          list={props.popularCamps}
          title="Popular Programs"
          onViewAll={() => handleViewAll('Popular Programs')}
        />
      )}

      {/* ── Starting Soon ── */}
      {props.startingSoon?.data?.length > 0 && <StartingSoon list={props.startingSoon} />}

      {/* ── Nearby ── */}
      {/* {props.nearbyCamps && <NearByCarousel list={props.nearbyCamps} />} */}

      {/* ── Advertisements ── */}
      {advList.length > 0 && (
        <section style={{ padding: '0 24px 48px', maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 24 }}>
            {advList.slice(0, 3).map(item => (
              <a key={item._id || item.link} href={item.link} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', flexShrink: 0 }}>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <img src={AddsIcon} alt="" style={{ position: 'absolute', right: 29, top: 26, zIndex: 2 }} />
                  <img src={item.image} alt={item.tagline} style={{ width: 350, height: 320, objectFit: 'cover', display: 'block' }} />
                  <div style={{ position: 'absolute', left: 45, top: 38, textAlign: 'left', zIndex: 2 }}>
                    <h4 style={{ background: ORANGE, color: '#fff', padding: '9px 15px', fontSize: 16, display: 'inline-block', margin: 0 }}>{item.tagline}</h4>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

const mapStateToProps = state => {
  const { allProgram }                 = state.home;
  const { lat, long }                  = state.pFilter;
  const { popularCamps, startingSoon, featured, nearbyCamps } = allProgram;
  const { preList }                    = state.program;
  return {
    popularCamps, startingSoon, featured, nearbyCamps,
    lat, long,
    category: preList?.category || [],
  };
};

const mapDispatchToProps = dispatch => ({
  toggleResetPasswordModal: () => dispatch(toggleResetPasswordModal()),
  getAllProgramListAction:  params => dispatch(getAllProgramListAction(params)),
  fFilterTypeAction:       params => dispatch(fFilterTypeAction(params)),
  fCategoriesAction:       params => dispatch(fCategoriesAction(params)),
  setSubCatAction:         params => dispatch(setSubCatAction(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Home);
